import {
  normalizeQuoteMediaMessage,
  ProviderSubscriptionStatus
} from "./market-data-contracts.js";

const SDK_VERSION = "2.71.1";
const HANDLED_EVENTS = [
  "error",
  "close",
  "slow",
  "stats",
  "initialDataSent",
  "missedDataSent",
  "resubscribeMessage",
  "reconnectSuccess",
  "reconnectMessage"
];

export class QuoteMediaProviderAdapter {
  constructor(handlers = {}) {
    this.handlers = handlers;
    this.stream = null;
    this.closed = false;
    this.streamer = globalThis.qmci?.Streamer;
  }

  async connect(grant) {
    if (this.closed) throw new Error("The QuoteMedia adapter is closed.");
    if (!this.streamer) {
      const sdkModule = await import("@quotemedia.com/streamer/qmci-streamer-2.71.1.js");
      this.streamer = resolveQuoteMediaStreamer(sdkModule);
    }
    if (!this.streamer) {
      throw new Error(`QuoteMedia streamer ${SDK_VERSION} did not initialize.`);
    }
    if (grant.authenticationScheme !== "Sid") {
      throw new TypeError("QuoteMedia requires a browser-safe SID grant.");
    }
    let stream;
    try {
      stream = await callbackPromise(callback => this.streamer.open({
        host: grant.streamUri,
        cors: true,
        rejectExcessiveConnection: true,
        conflation: null,
        format: "application/json",
        isReconnect: false,
        credentials: { sid: grant.sid }
      }, callback));
    } catch (error) {
      const code = providerErrorCode(error);
      if (code === 452) {
        const connectionLimit = new Error("The QuoteMedia connection limit was reached.");
        connectionLimit.reason = "connection-limit";
        throw connectionLimit;
      }
      if (code === 401 || code === 403) {
        const unauthorized = new Error("QuoteMedia rejected the streaming connection.");
        unauthorized.reason = "provider-unauthorized";
        unauthorized.status = code;
        throw unauthorized;
      }
      const failure = new Error("QuoteMedia could not open the streaming connection.");
      failure.reason = code === -2 || code === 0
        ? "provider-transport-failed"
        : code === 404 || code === 501
          ? "provider-host-unavailable"
          : error?.name === "TypeError"
            ? "provider-client-error"
            : "provider-open-failed";
      failure.terminal = [404, 501].includes(code) || error?.name === "TypeError";
      this.handlers.onDiagnostic?.({
        kind: "provider-open-failed",
        code,
        category: failure.reason
      });
      throw failure;
    }
    if (this.closed) {
      stream.close();
      throw new Error("The QuoteMedia adapter was closed while connecting.");
    }
    this.stream = stream;
    stream.on("message", raw => {
      const message = normalizeQuoteMediaMessage(
        raw,
        value => this.streamer.marketDataTypes.get(value),
        Date.now()
      );
      if (message) this.handlers.onMessage?.(message);
      else this.handlers.onDiagnostic?.({ kind: "unknown-message" });
    });
    for (const eventName of HANDLED_EVENTS) {
      stream.on(eventName, value => this.handlers.onEvent?.({
        kind: eventName,
        detail: safeEventDetail(eventName, value)
      }));
    }
    return this;
  }

  async subscribe(requests) {
    const results = [];
    for (const group of groupRequests(requests)) {
      let result;
      try {
        result = await callbackPromise(callback => this.stream.subscribe(
          group.requests.map(request => request.providerSymbol),
          [this.streamer.subscriptionTypes[group.type]],
          {
            conflation: group.conflation,
            skipHeavyInitialLoad: group.skipHeavyInitialLoad
          },
          callback
        ));
      } catch (error) {
        results.push(...group.requests.map(request => ({
          key: request.key,
          status: ProviderSubscriptionStatus.REJECTED,
          metadata: { reason: "provider-request-failed" }
        })));
        continue;
      }
      results.push(...classifySubscriptionResults(group.requests, result));
    }
    return results;
  }

  async unsubscribe(requests) {
    for (const group of groupRequests(requests)) {
      try {
        await callbackPromise(callback => this.stream.unsubscribe(
          group.requests.map(request => request.providerSymbol),
          [this.streamer.subscriptionTypes[group.type]],
          { conflation: group.conflation },
          callback
        ));
      } catch {
        // Desired state remains authoritative and will be rebuilt after reconnect.
      }
    }
  }

  getSessionStats() {
    this.stream?.getSessionStats?.();
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    const stream = this.stream;
    this.stream = null;
    try {
      stream?.close?.();
    } catch {
      // Disposal is best effort and never exposes provider details.
    }
  }
}

export function createQuoteMediaAdapter(handlers) {
  return new QuoteMediaProviderAdapter(handlers);
}

export function resolveQuoteMediaStreamer(sdkModule) {
  return globalThis.qmci?.Streamer ||
    sdkModule?.Streamer ||
    sdkModule?.default?.Streamer ||
    sdkModule?.default ||
    null;
}

function groupRequests(requests) {
  const groups = new Map();
  for (const item of requests) {
    const request = item.request || item;
    const key = [request.type, request.conflation ?? "default", request.skipHeavyInitialLoad].join("|");
    const group = groups.get(key) || {
      type: request.type,
      conflation: request.conflation,
      skipHeavyInitialLoad: request.skipHeavyInitialLoad,
      requests: []
    };
    group.requests.push({ ...request, key: item.key || item.subscriptionKey });
    groups.set(key, group);
  }
  return groups.values();
}

function classifySubscriptionResults(requests, result = {}) {
  const sets = {
    [ProviderSubscriptionStatus.SUBSCRIBED]: result.subscribed || [],
    [ProviderSubscriptionStatus.UNENTITLED]: result.unentitled || [],
    [ProviderSubscriptionStatus.REJECTED]: result.rejected || [],
    [ProviderSubscriptionStatus.INVALID_SYMBOL]: result.invalidSymbols || []
  };
  return requests.map(request => {
    for (const [status, values] of Object.entries(sets)) {
      const match = values.find(value => sameSubscription(value, request));
      if (match) {
        return {
          key: request.key,
          status,
          metadata: {
            entitlement: stringOrNull(match.entitlement),
            reason: status
          }
        };
      }
    }
    return {
      key: request.key,
      status: ProviderSubscriptionStatus.REJECTED,
      metadata: { reason: "provider-result-missing" }
    };
  });
}

function sameSubscription(value, request) {
  if (typeof value === "string") {
    return value.toUpperCase() === request.providerSymbol;
  }
  return String(value?.symbol || "").toUpperCase() === request.providerSymbol &&
    (!value?.type || String(value.type).toUpperCase() === request.type);
}

function safeEventDetail(kind, value) {
  if (kind === "stats") {
    return Object.freeze({
      numberOfAvailableSymbolsL1: finite(value?.numberOfAvailableSymbolsL1),
      numberOfAvailableConnections: finite(value?.numberOfAvailableConnections),
      numberOfOpenConnections: finite(value?.numberOfOpenConnections),
      numberOfSubscribedSymbols: finite(value?.numberOfSubscribedSymbols)
    });
  }
  if (kind === "reconnectSuccess") {
    return Object.freeze({
      previousConnectionId: stringOrNull(value?.previousConnectionId),
      currentConnectionId: stringOrNull(value?.currentConnectionId)
    });
  }
  return Object.freeze({ code: finite(value?.code), reason: kind });
}

function callbackPromise(run) {
  return new Promise((resolve, reject) => {
    run((error, result) => error ? reject(error) : resolve(result));
  });
}

function finite(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function providerErrorCode(value) {
  const number = Number(value?.code ?? value?.status ?? value?.details?.code);
  return Number.isFinite(number) ? number : null;
}

function stringOrNull(value) {
  const text = value === null || value === undefined ? "" : String(value).trim();
  return text || null;
}

export const QuoteMediaStreamerVersion = SDK_VERSION;
